// AerialExpandedView — full-frame satellite map (NLSC tiles) with
// outdoor / geo-pinned location markers and a building chip row.
//
// docs/specs/spatial-hierarchy-v2.md §6.1
//
// Why re-render tiles via MapLibre instead of streaming the stored
// R2 stitch:
//   - the stitch was made for ML pipelines (full bbox at fixed zoom)
//     and doesn't pan / zoom interactively
//   - MapLibre + NLSC tiles is already wired in SatelliteMapPicker —
//     reusing the same tile sources avoids duplicating R2 serving
//     infrastructure (no presigned-GET endpoint, no public bucket)
//   - the operator gets pan / zoom / lat-lon picking for free
// The stored capture row tells us bbox + zoom + tile_source, so we
// can land the user on the exact frame the satellite_hd / model_3d
// chain was generated from.

import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { ArrowLeft, Building2, MapPin } from "lucide-react";
import { listLocations, friendlyLocationsError, type LocationRow } from "../lib/locationsApi.js";
import type { SatelliteCapture } from "../lib/visualLayersApi.js";
import type { BuildingDto, BuildingFloorDto, FloorStatusSnapshotDto } from "../lib/floorStackApi.js";

interface BuildingWithFloors extends BuildingDto {
  floors: Array<BuildingFloorDto & { latest_snapshot: FloorStatusSnapshotDto | null }>;
}

interface Props {
  siteId: string;
  siteName: string;
  tenantId: string | null;
  capture: SatelliteCapture;
  buildings: BuildingWithFloors[];
  /** Tells the parent to swap the active building and collapse back to
   *  the floor-stack tier. Receives null to mean「拉回鳥瞰預設 building」. */
  onSelectBuilding: (buildingId: string | null) => void;
  onBack: () => void;
}

const TILE_SOURCES: Record<string, string[]> = {
  nlsc_photo_mix: ["https://wmts.nlsc.gov.tw/wmts/PHOTO_MIX/default/GoogleMapsCompatible/{z}/{y}/{x}"],
  nlsc_emap:      ["https://wmts.nlsc.gov.tw/wmts/EMAP/default/GoogleMapsCompatible/{z}/{y}/{x}"],
};

export default function AerialExpandedView({
  siteId, siteName, tenantId, capture, buildings, onSelectBuilding, onBack,
}: Props): React.ReactElement {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);

  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Fetch locations with lat/lon for this site ────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await listLocations({ siteId, tenantId, limit: 500 });
        if (cancelled) return;
        setLocations(r.items.filter((c) => c.lat !== null && c.lon !== null));
      } catch (e) {
        if (!cancelled) setError(friendlyLocationsError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  // ── Init map ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current) return;
    const tiles = TILE_SOURCES[capture.capture_tile_source] ?? TILE_SOURCES.nlsc_photo_mix;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          "nlsc-tiles": { type: "raster", tiles, tileSize: 256, maxzoom: 20 },
        },
        layers: [{ id: "nlsc-layer", type: "raster", source: "nlsc-tiles" }],
      },
      bounds: [
        [capture.capture_bbox_sw_lon, capture.capture_bbox_sw_lat],
        [capture.capture_bbox_ne_lon, capture.capture_bbox_ne_lat],
      ],
      fitBoundsOptions: { padding: 48 },
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "© 內政部國土測繪中心" }), "bottom-left");
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, [capture.capture_tile_source, capture.capture_bbox_sw_lon, capture.capture_bbox_sw_lat, capture.capture_bbox_ne_lon, capture.capture_bbox_ne_lat]);

  // ── Render location markers ──────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Tear down old markers before re-adding.
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
    for (const cp of locations) {
      if (cp.lat === null || cp.lon === null) continue;
      const el = document.createElement("div");
      el.className = "aerial-marker";
      el.setAttribute("data-location-id", cp.id);
      el.title = `${cp.code} — ${cp.label}`;
      el.style.cssText = [
        "width:18px","height:18px","border-radius:50%",
        "background:rgba(96,165,250,0.85)",
        "border:2px solid #fff",
        "box-shadow:0 0 0 1px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.35)",
        "cursor:pointer","transition:transform 120ms ease",
      ].join(";");
      el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.25)"; });
      el.addEventListener("mouseleave", () => { el.style.transform = "scale(1.0)"; });
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([cp.lon, cp.lat])
        .setPopup(new maplibregl.Popup({ offset: 18, closeButton: false }).setHTML(
          `<div style="font-size:12px;line-height:1.4">
            <div style="font-family:ui-monospace,monospace;font-weight:600">${escapeHtml(cp.code)}</div>
            <div style="color:#666">${escapeHtml(cp.label)}</div>
            ${cp.geofence_radius_m ? `<div style="color:#888;font-size:10px;margin-top:2px">Geofence ${cp.geofence_radius_m}m</div>` : ""}
          </div>`,
        ))
        .addTo(map);
      markersRef.current.push(marker);
    }
  }, [locations]);

  const sortedBuildings = useMemo(
    () => [...buildings].sort((a, b) => a.display_order - b.display_order),
    [buildings],
  );

  const captureTimestamp = useMemo(() => {
    try {
      return new Date(capture.created_at).toISOString().replace("T", " ").slice(0, 16) + "Z";
    } catch { return capture.created_at; }
  }, [capture.created_at]);

  return (
    <div className="aerial-expanded-view" data-testid="aerial-expanded-view">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="btn-secondary text-xs">
            <ArrowLeft className="h-3.5 w-3.5" /> 回樓層檢視
          </button>
          <h2 className="text-base font-semibold text-text-primary truncate">
            🛰️ {siteName} ・ 鳥瞰層
          </h2>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <span className="rounded border border-border bg-bg-raised px-2 py-0.5 text-2xs uppercase tracking-wider">
            {qualityLabel(capture.source_kind)}
          </span>
          <span className="text-text-muted">擷取於 {captureTimestamp}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
        {/* Map */}
        <div className="relative rounded-lg border border-border overflow-hidden bg-bg-raised"
             style={{ minHeight: "480px" }}>
          <div ref={mapContainerRef} className="absolute inset-0" data-testid="aerial-map" />
        </div>

        {/* Side panel */}
        <aside className="space-y-3">
          <div className="rounded-lg border border-border bg-bg-surface p-3">
            <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5 text-accent" /> 建築（{sortedBuildings.length}）
            </h3>
            {sortedBuildings.length === 0 ? (
              <p className="text-2xs text-text-muted">此案場尚未建立建築。</p>
            ) : (
              <ul className="space-y-1">
                {sortedBuildings.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      onClick={() => onSelectBuilding(b.id)}
                      className="w-full rounded border border-border bg-bg-raised px-2 py-1.5 text-left text-xs hover:bg-bg-hover transition-colors flex items-center justify-between gap-2"
                      data-building-id={b.id}
                    >
                      <span className="truncate">
                        <span className="text-text-primary font-medium">{b.name}</span>
                        <span className="ml-1 text-text-muted">·</span>
                        <span className="ml-1 text-text-muted">{b.floors_count} 層</span>
                      </span>
                      <span className="text-text-muted text-2xs">進入 →</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-border bg-bg-surface p-3">
            <h3 className="text-xs font-semibold text-text-primary mb-2 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-accent" /> 已標記地點（{locations.length}）
            </h3>
            {loading && <p className="text-2xs text-text-muted">載入中…</p>}
            {error && <p className="text-2xs text-danger">{error}</p>}
            {!loading && !error && locations.length === 0 && (
              <p className="text-2xs text-text-muted leading-relaxed">
                此案場目前沒有任何標記了經緯度的地點。可在「樓層圖資編輯 → 編輯地點」內設定座標。
              </p>
            )}
            {!loading && !error && locations.length > 0 && (
              <ul className="space-y-1 max-h-[260px] overflow-y-auto pr-1">
                {locations.map((cp) => (
                  <li key={cp.id} className="rounded border border-border bg-bg-raised px-2 py-1 text-2xs flex items-center justify-between gap-2">
                    <span className="truncate">
                      <span className="font-mono text-text-primary">{cp.code}</span>
                      <span className="ml-1 text-text-secondary">{cp.label}</span>
                    </span>
                    <button
                      type="button"
                      className="text-accent hover:underline text-[10px] flex-shrink-0"
                      onClick={() => panToLocation(mapRef.current, cp)}
                    >
                      定位
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function panToLocation(map: maplibregl.Map | null, cp: LocationRow): void {
  if (!map || cp.lat === null || cp.lon === null) return;
  map.easeTo({ center: [cp.lon, cp.lat], zoom: Math.max(map.getZoom(), 19), duration: 600 });
}

function qualityLabel(sourceKind: string): string {
  switch (sourceKind) {
    case "satellite_capture": return "衛星 RAW";
    case "satellite_hd":      return "衛星 HD";
    case "model_3d":          return "3D 模型";
    default:                  return sourceKind;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]!));
}
